import { Observable } from 'rxjs/Observable';
import { Selector } from '@ngrx/store/src/models';
import { Store } from '@ngrx/store';
import { catchError, concatMap, filter, mergeMap, share, take, tap } from 'rxjs/operators';
import { throwError } from 'rxjs';

import { AGeneralActionCreator } from './general-action-creator.abstract';
import { ObjectUtils } from '@ispm/shared/utils/Object.utils';

export abstract class ActionCreatorAbstract<Type = any> {

  protected data$: Observable<Type>;
  private awaitedStateUpdates: { [hash: string]: Observable<any> } = { };
  private readonly rootPath: string;

  // should be 'protected store: Store<IgenomicsState>' when the 'backward compatible' becomes not relevant
  protected constructor(
    protected store: Store<any>,
    ...pathOrSelectors: Array<string | any>
  ) {
    this.rootPath = pathOrSelectors.length && pathOrSelectors.toString().replace(',', '.') || '';
    this.data$ = pathOrSelectors.length
      ? this.store.select(state => ObjectUtils.walk(state, this.rootPath))
      : this.store;
    this.store = this.data$ as any; // backward compatible
  }

  getState<StateNodeType = any>(...props): Observable<StateNodeType> {
    let state: any = this.data$;

    if (props.length > 0) {
      // state = state.select(_state => ObjectUtils.walk(_state, props.join('.')));
      state = state.select(...props);
    }

    return state
      .pipe(
        mergeMap(value => this.handleStateValue(props, value))
      );
  }

  // get state, trigger updateState$ if: data is empty & there is no awaited updateState$ for the selector
  // $updateState implied as REST update piped with the store point value update
  restoreState<StateNodeType = any>(
    pathOrSelector: string | (() => Selector<any, any>),
    updateState$: Observable<any>
  ): Observable<StateNodeType> {

    const path = pathOrSelector.toString();
    const hash = this.rootPath + '.' + path;
    const stateSelector = () => typeof pathOrSelector === 'string' ?
      this.getState<StateNodeType>(path) :
      this.data$.pipe(pathOrSelector()) as Observable<StateNodeType>;

    if (this.awaitedStateUpdates[hash]) {
      return this.awaitedStateUpdates[hash].pipe(
        take(1),
        concatMap(() => stateSelector()),
        filter(_value => !!_value),
      );
    }

    return stateSelector().pipe(
      take(1),
      concatMap(value => {
        if (!value) {
          this.awaitedStateUpdates[hash] = updateState$.pipe(
            take(1),
            concatMap(() => stateSelector()),
            filter(_value => !!_value),
            // takeWhile(_value => !!_value),
            tap(() => (delete this.awaitedStateUpdates[hash])),
            catchError(err => {
              console.error('Failed to select State: Error', err.message);
              delete this.awaitedStateUpdates[hash];
              return throwError(err);
            }),
            share(),
          );
        }

        if (this.awaitedStateUpdates[hash]) {
          return this.restoreState<StateNodeType>(pathOrSelector, updateState$);
        }

        return stateSelector();
      }),
    );
  }

  handleStateValue(props: Array<string>, state: any): Promise<any> | Observable<any> {
    return state instanceof Observable || state instanceof Promise
      ? state
      : Promise.resolve(state);
  }

  dispatch(action: AGeneralActionCreator) {
    this.store.dispatch(action);
  }
}
